import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { getVideos } from '../api';
import { getYouTubeId, getThumbnailUrl } from '../utils/youtube';

import image1 from './../images/web/hero-1.webp';
import image2 from './../images/web/hero-2.webp';
import image3 from './../images/web/hero-3.webp';
import image4 from './../images/web/hero-4.webp';
import bioImage from './../images/web/bio.webp';

const heroImages = [image1, image2, image3, image4];

const Home = ({ urls }) => {
   const [currentIndex, setCurrentIndex] = useState(0);
   const [videos, setVideos] = useState([]);

   useEffect(() => {
      const timer = setInterval(() => {
         setCurrentIndex((prev) => (prev + 1) % heroImages.length);
      }, 5000);
      return () => clearInterval(timer);
   }, []);

   useEffect(() => {
      loadData();
   }, []);

   const loadData = async () => {
      try {
         const videosData = await getVideos();
         setVideos(videosData.slice(0, 3));
      } catch (err) {
         console.error('Failed to load data:', err);
      }
   };

   const fadeInUp = {
      initial: { opacity: 0, y: 40 },
      whileInView: { opacity: 1, y: 0 },
      viewport: { once: true, margin: '-100px' },
      transition: { duration: 0.8 },
   };

   return (
      <div>
         {/* ===== HERO SECTION ===== */}
         <section style={styles.heroSection} className="hero-section">
            <div style={styles.imageContainer}>
               <AnimatePresence mode="wait">
                  <motion.div
                     key={currentIndex}
                     initial={{ opacity: 0, scale: 1.1 }}
                     animate={{ opacity: 1, scale: 1 }}
                     exit={{ opacity: 0 }}
                     transition={{ duration: 1.5, ease: 'easeOut' }}
                     style={styles.imageWrapper}
                  >
                     <div
                        style={{
                           ...styles.backgroundImage,
                           backgroundImage: `url(${heroImages[currentIndex]})`,
                        }}
                     />
                  </motion.div>
               </AnimatePresence>
               <div style={styles.overlay} />
            </div>

            <div style={styles.heroContent}>
               <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 0.5 }}
                  style={styles.textContainer}
               >
                  <motion.p
                     initial={{ opacity: 0 }}
                     animate={{ opacity: 1 }}
                     transition={{ duration: 1, delay: 0.8 }}
                     style={styles.subtitle}
                  >
                     피아니스트 김예라
                  </motion.p>
                  <motion.h1
                     initial={{ opacity: 0, y: 20 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 1, delay: 1 }}
                     style={styles.title}
                  >
                     Yera Kim
                  </motion.h1>
                  <motion.div
                     initial={{ width: 0 }}
                     animate={{ width: 80 }}
                     transition={{ duration: 1, delay: 1.3 }}
                     style={styles.divider}
                  />
                  <motion.p
                     initial={{ opacity: 0 }}
                     animate={{ opacity: 1 }}
                     transition={{ duration: 1, delay: 1.5 }}
                     style={styles.tagline}
                  >
                     "Music is the divine way to tell beautiful, poetic things to the heart"
                  </motion.p>
               </motion.div>
            </div>

            <div style={styles.indicators}>
               {heroImages.map((_, index) => (
                  <button
                     key={index}
                     onClick={() => setCurrentIndex(index)}
                     style={{
                        ...styles.indicator,
                        ...(index === currentIndex ? styles.indicatorActive : {}),
                     }}
                     aria-label={`Go to slide ${index + 1}`}
                  />
               ))}
            </div>

            <motion.div
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               transition={{ duration: 1, delay: 2 }}
               style={styles.scrollIndicator}
            >
               <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5">
                     <path d="M12 5v14M5 12l7 7 7-7" />
                  </svg>
               </motion.div>
            </motion.div>
         </section>

         {/* ===== BIOGRAPHY SECTION ===== */}
         <section style={styles.section} className="home-section">
            <div style={styles.container}>
               <div style={styles.bioGrid}>
                  <motion.div {...fadeInUp} style={styles.bioImageWrapper}>
                     <img src={bioImage} alt="피아니스트 김예라" style={styles.bioImage} />
                  </motion.div>
                  <motion.div {...fadeInUp} style={styles.bioContent}>
                     <p style={styles.sectionLabel}>About</p>
                     <h2 style={styles.sectionTitle}>Biography</h2>
                     <div style={styles.goldDivider} />
                     <p style={styles.bioText}>
                        섬세한 테크닉과 다채로운 표현력을 선보이고 있는 피아니스트 김예라는
                        중앙대학교를 성적우수로 졸업하고, 텍사스 베일러대학교에서 석사를 만점으로,
                        펜실베니아 주립대학교에서 박사학위를 취득하였습니다.
                     </p>
                     <p style={styles.bioText}>
                        New York International Music Competition, United States International Music Competition 등
                        다수의 국제 콩쿠르에서 1위를 수상하였으며, Carnegie Hall, Lincoln Memorial 등에서 연주하였습니다.
                     </p>
                     <Link to="/biography" style={styles.linkButton}>
                        Read More
                     </Link>
                  </motion.div>
               </div>
            </div>
         </section>

         {/* ===== VIDEOS SECTION ===== */}
         <section style={styles.sectionDark} className="home-section">
            <div style={styles.container}>
               <motion.div {...fadeInUp} style={styles.sectionHeader}>
                  <p style={styles.sectionLabelLight}>Watch</p>
                  <h2 style={styles.sectionTitleLight}>Videos</h2>
                  <div style={styles.goldDividerCenter} />
               </motion.div>

               <div style={styles.videosGrid}>
                  {videos.map((video, index) => {
                     const ytId = getYouTubeId(video.url);
                     return (
                        <motion.div
                           key={video.id}
                           initial={{ opacity: 0, y: 40 }}
                           whileInView={{ opacity: 1, y: 0 }}
                           viewport={{ once: true }}
                           transition={{ duration: 0.6, delay: index * 0.1 }}
                           style={styles.videoCard}
                           className="video-card"
                        >
                           {ytId ? (
                              <Link to="/videos" style={styles.videoCardLink}>
                                 <div style={styles.videoWrapper}>
                                    <img
                                       src={getThumbnailUrl(ytId)}
                                       alt={video.title}
                                       loading="lazy"
                                       style={styles.thumbImage}
                                    />
                                    <div style={styles.playOverlay}>
                                       <svg width="64" height="64" viewBox="0 0 64 64" aria-hidden="true">
                                          <circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.45)" />
                                          <path d="M26 20 L46 32 L26 44 Z" fill="#ffffff" />
                                       </svg>
                                    </div>
                                 </div>
                                 <div style={styles.videoInfo}>
                                    <h3 style={styles.videoTitle}>{video.title}</h3>
                                 </div>
                              </Link>
                           ) : (
                              <>
                                 <div style={styles.videoWrapper}>
                                    <iframe
                                       src={video.url}
                                       title={video.title}
                                       frameBorder="0"
                                       allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                       allowFullScreen
                                       style={styles.iframe}
                                    />
                                 </div>
                                 <div style={styles.videoInfo}>
                                    <h3 style={styles.videoTitle}>{video.title}</h3>
                                 </div>
                              </>
                           )}
                        </motion.div>
                     );
                  })}
               </div>

               <motion.div {...fadeInUp} style={styles.centerLink}>
                  <Link to="/videos" style={styles.linkButtonLight}>
                     View All Videos
                  </Link>
               </motion.div>
            </div>
         </section>

         {/* ===== EVENTS SECTION - Hidden for now =====
         <section style={styles.sectionCream} className="home-section">
            <div style={styles.container}>
               <motion.div {...fadeInUp} style={styles.sectionHeader}>
                  <p style={styles.sectionLabel}>Performances</p>
                  <h2 style={styles.sectionTitle}>Recent Events</h2>
                  <div style={styles.goldDividerCenter} />
               </motion.div>

               <div style={styles.eventsGrid}>
                  {events.map((event, index) => (
                     <motion.div
                        key={event.id}
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        style={styles.eventCard}
                     >
                        <p style={styles.eventDate}>{event.date}</p>
                        <h3 style={styles.eventTitle}>{event.title}</h3>
                        <p style={styles.eventVenue}>{event.venue}</p>
                        {event.time && <p style={styles.eventTime}>{event.time}</p>}
                     </motion.div>
                  ))}
               </div>

               <motion.div {...fadeInUp} style={styles.centerLink}>
                  <Link to="/events" style={styles.linkButton}>
                     View All Events
                  </Link>
               </motion.div>
            </div>
         </section>
         */}

         {/* ===== CONTACT SECTION ===== */}
         <section style={styles.sectionDark} className="home-section">
            <div style={styles.container}>
               <motion.div {...fadeInUp} style={styles.contactContent}>
                  <p style={styles.sectionLabelLight}>Get in Touch</p>
                  <h2 style={styles.sectionTitleLight}>Contact</h2>
                  <div style={styles.goldDividerCenter} />
                  <p style={styles.contactText}>
                     For inquiries about performances, collaborations, or lessons,<br />
                     please feel free to reach out.
                  </p>
                  <div style={styles.socialLinks}>
                     <a
                        href="https://instagram.com/ye___ra"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={styles.socialLink}
                     >
                        Instagram
                     </a>
                     <span style={styles.socialDivider}>|</span>
                     <a
                        href="https://youtube.com/@yerakim3956"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={styles.socialLink}
                     >
                        YouTube
                     </a>
                  </div>
                  <Link to="/contact" style={styles.linkButtonLight}>
                     Send a Message
                  </Link>
               </motion.div>
            </div>
         </section>

         {/* ===== FOOTER ===== */}
         <footer style={styles.footer}>
            <p style={styles.footerText}>© {new Date().getFullYear()} Yera Kim. All rights reserved.</p>
         </footer>
      </div>
   );
};

const styles = {
   // Hero Section
   heroSection: {
      position: 'relative',
      width: '100vw',
      maxWidth: '100%',
      height: '100vh',
      overflow: 'hidden',
   },
   imageContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
   },
   imageWrapper: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
   },
   backgroundImage: {
      width: '100%',
      height: '100%',
      backgroundSize: 'cover',
      backgroundPosition: 'center 25%',
   },
   overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.5) 100%)',
   },
   heroContent: {
      position: 'relative',
      zIndex: 10,
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
   },
   textContainer: {
      textAlign: 'center',
      color: 'white',
      padding: '0 20px',
   },
   subtitle: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.9rem',
      letterSpacing: '0.4em',
      textTransform: 'uppercase',
      marginBottom: '20px',
      fontWeight: 300,
   },
   title: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: 'clamp(3rem, 10vw, 7rem)',
      fontWeight: 300,
      letterSpacing: '0.1em',
      margin: 0,
      lineHeight: 1.1,
   },
   divider: {
      height: '1px',
      backgroundColor: '#c9a227',
      margin: '30px auto',
   },
   tagline: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: 'clamp(1rem, 2vw, 1.3rem)',
      fontStyle: 'italic',
      fontWeight: 300,
      maxWidth: '600px',
      margin: '0 auto',
      lineHeight: 1.6,
   },
   indicators: {
      position: 'absolute',
      bottom: '100px',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      gap: '12px',
      zIndex: 20,
   },
   indicator: {
      width: '40px',
      height: '3px',
      backgroundColor: 'rgba(255, 255, 255, 0.4)',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      padding: 0,
   },
   indicatorActive: {
      backgroundColor: '#c9a227',
   },
   scrollIndicator: {
      position: 'absolute',
      bottom: '30px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 20,
   },

   // Common Section Styles
   section: {
      padding: '120px 0',
      backgroundColor: '#fafafa',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
   },
   sectionDark: {
      padding: '120px 0',
      backgroundColor: '#0a0a0a',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
   },
   sectionCream: {
      padding: '120px 0',
      backgroundColor: '#f5f0e8',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
   },
   container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 5%',
      boxSizing: 'border-box',
      width: '100%',
   },
   sectionHeader: {
      textAlign: 'center',
      marginBottom: '60px',
   },
   sectionLabel: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.75rem',
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
      color: '#c9a227',
      marginBottom: '15px',
   },
   sectionLabelLight: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.75rem',
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
      color: '#c9a227',
      marginBottom: '15px',
   },
   sectionTitle: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: 'clamp(2rem, 5vw, 3rem)',
      fontWeight: 300,
      color: '#0a0a0a',
      margin: 0,
   },
   sectionTitleLight: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: 'clamp(2rem, 5vw, 3rem)',
      fontWeight: 300,
      color: 'white',
      margin: 0,
   },
   goldDivider: {
      width: '60px',
      height: '1px',
      backgroundColor: '#c9a227',
      marginTop: '20px',
      marginBottom: '30px',
   },
   goldDividerCenter: {
      width: '60px',
      height: '1px',
      backgroundColor: '#c9a227',
      margin: '20px auto 0',
   },

   // Biography Section
   bioGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '60px',
      alignItems: 'center',
   },
   bioImageWrapper: {
      position: 'relative',
   },
   bioImage: {
      width: '100%',
      height: 'auto',
      display: 'block',
   },
   bioContent: {
      paddingTop: '20px',
   },
   bioText: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '1rem',
      lineHeight: 1.9,
      color: '#555',
      marginBottom: '20px',
      fontWeight: 300,
   },
   linkButton: {
      display: 'inline-block',
      marginTop: '20px',
      padding: '15px 40px',
      border: '1px solid #0a0a0a',
      color: '#0a0a0a',
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.8rem',
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      textDecoration: 'none',
      transition: 'all 0.4s ease',
   },
   linkButtonLight: {
      display: 'inline-block',
      marginTop: '20px',
      padding: '15px 40px',
      border: '1px solid white',
      color: 'white',
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.8rem',
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      textDecoration: 'none',
      transition: 'all 0.4s ease',
   },
   centerLink: {
      textAlign: 'center',
      marginTop: '50px',
   },

   // Videos Section
   videosGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
      gap: '30px',
   },
   videoCard: {
      backgroundColor: '#1a1a1a',
      overflow: 'hidden',
      borderRadius: '8px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
   },
   videoCardLink: {
      display: 'block',
      color: 'inherit',
      textDecoration: 'none',
   },
   videoWrapper: {
      position: 'relative',
      paddingTop: '56.25%',
      backgroundColor: '#0a0a0a',
   },
   iframe: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      border: 'none',
   },
   thumbImage: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
   },
   playOverlay: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
   },
   videoInfo: {
      padding: '20px',
   },
   videoCategory: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.7rem',
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: '#c9a227',
   },
   videoTitle: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: '1.2rem',
      fontWeight: 400,
      color: 'white',
      marginTop: '8px',
   },

   // Events Section
   eventsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '30px',
   },
   eventCard: {
      backgroundColor: 'white',
      padding: '35px',
      borderLeft: '3px solid #c9a227',
   },
   eventDate: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.8rem',
      letterSpacing: '0.1em',
      color: '#c9a227',
      marginBottom: '12px',
      textTransform: 'uppercase',
   },
   eventTitle: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: '1.4rem',
      fontWeight: 500,
      color: '#0a0a0a',
      marginBottom: '8px',
   },
   eventVenue: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.9rem',
      fontWeight: 300,
      color: '#666',
   },
   eventTime: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.85rem',
      fontWeight: 300,
      color: '#888',
      marginTop: '5px',
   },

   // Contact Section
   contactContent: {
      textAlign: 'center',
      maxWidth: '600px',
      margin: '0 auto',
   },
   contactText: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '1rem',
      fontWeight: 300,
      color: '#888',
      lineHeight: 1.8,
      marginTop: '30px',
      marginBottom: '30px',
   },
   socialLinks: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '20px',
      marginBottom: '30px',
   },
   socialLink: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.9rem',
      fontWeight: 300,
      color: 'white',
      textDecoration: 'none',
      transition: 'color 0.3s ease',
   },
   socialDivider: {
      color: '#444',
   },

   // Footer
   footer: {
      backgroundColor: '#050505',
      padding: '40px 0',
      textAlign: 'center',
      width: '100%',
      maxWidth: '100vw',
   },
   footerText: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.8rem',
      fontWeight: 300,
      color: '#555',
   },
};

export default Home;
